import React, { useState, useEffect } from "react";
import { SessionDevice, DevicePricing } from "../api/contracts/deviceTypes";
import { editDevice, deleteDevice } from "../api/client";
import { DueTimeSelector } from "../components/DueTimeSelector";

type Props = {
  device: SessionDevice;
  onCancel: () => void;
  onSaved: () => Promise<void>;
};

export const EditDevice: React.FC<Props> = ({
  device,
  onCancel,
  onSaved,
}) => {
  const CAPACITY_VALUES = Array.from({ length: 8 }, (_, i) => 50 + i * 50);

  const [showPricingCard, setShowPricingCard] = useState(false);

  const [deviceName, setDeviceName] = useState(device.deviceName);
  const [capacityLiters, setCapacityLiters] = useState(device.capacityLiters);

  const [shellyServer, setShellyServer] = useState("");
  const [shellyAuthKey, setShellyAuthKey] = useState("");
  const [shellyAuthKeyTouched, setShellyAuthKeyTouched] = useState(false);

  const [shellyDeviceId, setShellyDeviceId] = useState("");
  const [shellyDeviceIdTouched, setShellyDeviceIdTouched] = useState(false);

  const [shellyChannel, setShellyChannel] = useState("0");

  const [dueTimes, setDueTimes] = useState<string[]>(
    (device.dueTimesLocal ?? []).map((t) => t.slice(0, 5))
  );

  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) =>
    `${h.toString().padStart(2, "0")}:00`
  );

  const [pricing, setPricing] = useState<DevicePricing>({
    energy: {
      model: "spot",
      markup: undefined,
      peakPrice: undefined,
      offpeakPrice: undefined,
      peakStart: "",
      offpeakStart: "",
    },
    transmission: {
      peakPrice: undefined,
      offpeakPrice: undefined,
      peakStart: "",
      offpeakStart: "",
    },
  });

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setDeviceName(device.deviceName);
    setCapacityLiters(device.capacityLiters);

    setDueTimes(
      (device.dueTimesLocal ?? []).map((t) => t.slice(0, 5))
    );

      function fromMwh(v?: number | null): number | undefined {
        if (v === null || v === undefined) {
          return undefined;
        }
        return Math.round((v / 10) * 100) / 100;
      }

    if (device.pricing) {
      setPricing({
        energy: {
          model: device.pricing.energy.model,

          markup: fromMwh(device.pricing.energy.markup),

          peakPrice: fromMwh(device.pricing.energy.peakPrice),
          offpeakPrice: fromMwh(device.pricing.energy.offpeakPrice),

          peakStart: device.pricing.energy.peakStart ?? "",
          offpeakStart: device.pricing.energy.offpeakStart ?? "",
        },

        transmission: {
          peakPrice: fromMwh(device.pricing.transmission.peakPrice),

          offpeakPrice: fromMwh(
            device.pricing.transmission.offpeakPrice
          ),

          peakStart:
            device.pricing.transmission.peakStart ?? "",

          offpeakStart:
            device.pricing.transmission.offpeakStart ?? "",
        },
      });
    }
  }, [device]);

  const save = async () => {
    setSaving(true);
    setError(null);

    try {
      await editDevice({
        deviceId: device.deviceId,
        deviceName,
        capacityLiters,
        dueTimesLocal: dueTimes,
        shellyServer,
        shellyAuthKey,
        shellyDeviceId,
        shellyChannel: Number(shellyChannel),
        pricing,
      });

      await onSaved();
    } catch (e) {
      console.error(e);
      setError("Failed to update device");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this device?")) return;

    setDeleting(true);

    try {
      await deleteDevice(device.deviceId);
      await onSaved();
    } catch (e) {
      console.error(e);
      setError("Failed to delete device");
    } finally {
      setDeleting(false);
    }
  };

  const parseNumberInput = (
    value: string
  ): number | undefined => {
    if (value.trim() === "") {
      return undefined;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : undefined;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 mt-6">

      {/* CARD 1 */}
      {!showPricingCard && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">

          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Edit device
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Physical device and Shelly configuration
            </p>
          </div>

          {/* Device Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Device name
            </label>

            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Capacity
            </label>

            <select
              value={capacityLiters}
              onChange={(e) => setCapacityLiters(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CAPACITY_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v} L
                </option>
              ))}
            </select>
          </div>

          {/* Shelly */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-600">
              Shelly connection
            </h3>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Shelly server
              </label>

              <input
                type="text"
                value={shellyServer}
                onChange={(e) => setShellyServer(e.target.value)}
                placeholder="https://shelly.cloud"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Auth key
              </label>

              <input
                type="text"
                value={shellyAuthKeyTouched ? shellyAuthKey : "••••••••"}
                onFocus={() => setShellyAuthKeyTouched(true)}
                onChange={(e) => setShellyAuthKey(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Device ID
                </label>

                <input
                  type="text"
                  value={shellyDeviceIdTouched ? shellyDeviceId : "••••••••"}
                  onFocus={() => setShellyDeviceIdTouched(true)}
                  onChange={(e) => setShellyDeviceId(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Channel
                </label>

                <select
                  value={shellyChannel}
                  onChange={(e) => setShellyChannel(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {["0", "1", "2", "3"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4">

            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-md bg-gray-200 text-sm hover:bg-gray-300"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => setShowPricingCard(true)}
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              Continue to pricing
            </button>

          </div>
        </div>
      )}

      {/* CARD 2 */}
      {showPricingCard && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-6">

          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Pricing setup
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Configure pricing model and due times
            </p>
          </div>

          {/* Due times */}
          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-600">
              Due times
            </h3>

            <DueTimeSelector
              dueTimes={dueTimes}
              onChange={setDueTimes}
            />
          </div>

          {/* ENERGY */}
          <div className="space-y-4 border rounded-lg p-4">

            <h3 className="text-sm font-semibold text-gray-700">
              Energy pricing
            </h3>

            <select
              value={pricing.energy.model}
              onChange={(e) =>
                setPricing({
                  ...pricing,
                  energy: {
                    ...pricing.energy,
                    model: e.target.value as "spot" | "fixed",
                  },
                })
              }
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="spot">Spot</option>
              <option value="fixed">Fixed</option>
            </select>

            {pricing.energy.model === "spot" && (
              <input
                type="number"
                step="0.01"
                placeholder="Markup c€/kWh"
                value={pricing.energy.markup ?? ""}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    energy: {
                      ...pricing.energy,
                      markup:  parseNumberInput(e.target.value),
                    },
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            )}

            {pricing.energy.model === "fixed" && (
              <div className="space-y-4">

                <div className="grid grid-cols-2 gap-3">

                  <input
                    type="number"
                    step="0.01"
                    placeholder="Peak price c€/kWh"
                    value={pricing.energy.peakPrice ?? ""}
                    onChange={(e) =>
                      setPricing({
                        ...pricing,
                        energy: {
                          ...pricing.energy,
                          peakPrice: parseNumberInput(e.target.value),
                        },
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />

                  <input
                    type="number"
                    step="0.01"
                    placeholder="Offpeak price c€/kWh"
                    value={pricing.energy.offpeakPrice ?? ""}
                    onChange={(e) =>
                      setPricing({
                        ...pricing,
                        energy: {
                          ...pricing.energy,
                          offpeakPrice: parseNumberInput(e.target.value),
                        },
                      })
                    }
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />

                </div>

                <div className="grid grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Peak starts
                  </label>

                  <select
                    value={pricing.energy.peakStart}
                    onChange={(e) =>
                      setPricing({
                        ...pricing,
                        energy: {
                          ...pricing.energy,
                          peakStart: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select hour</option>

                    {HOUR_OPTIONS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Off-peak starts
                  </label>

                  <select
                    value={pricing.energy.offpeakStart}
                    onChange={(e) =>
                      setPricing({
                        ...pricing,
                        energy: {
                          ...pricing.energy,
                          offpeakStart: e.target.value,
                        },
                      })
                    }
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                    focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select hour</option>

                    {HOUR_OPTIONS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>

                </div>
              </div>
            )}
          </div>

          {/* TRANSMISSION */}
          <div className="space-y-4 border rounded-lg p-4">

            <h3 className="text-sm font-semibold text-gray-700">
              Transmission pricing
            </h3>

            <div className="grid grid-cols-2 gap-3">

              <input
                type="number"
                step="0.01"
                placeholder="Peak price c€/kWh"
                value={pricing.transmission.peakPrice ?? ""}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    transmission: {
                      ...pricing.transmission,
                      peakPrice: parseNumberInput(e.target.value),
                    },
                  })
                }
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />

              <input
                type="number"
                step="0.01"
                placeholder="Offpeak price c€/kWh"
                value={pricing.transmission.offpeakPrice ?? ""}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    transmission: {
                      ...pricing.transmission,
                      offpeakPrice: parseNumberInput(e.target.value), 
                    },
                  })
                }
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              />

            </div>

            <div className="grid grid-cols-2 gap-4">

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Peak starts
              </label>

              <select
                value={pricing.transmission.peakStart}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    transmission: {
                      ...pricing.transmission,
                      peakStart: e.target.value,
                    },
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select hour</option>

                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Off-peak starts
              </label>

              <select
                value={pricing.transmission.offpeakStart}
                onChange={(e) =>
                  setPricing({
                    ...pricing,
                    transmission: {
                      ...pricing.transmission,
                      offpeakStart: e.target.value,
                    },
                  })
                }
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select hour</option>

                {HOUR_OPTIONS.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </div>

            </div>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600">
              {error}
            </p>
          )}

          {/* Buttons */}
          <div className="flex justify-between pt-4">

            <button
              type="button"
              onClick={() => setShowPricingCard(false)}
              className="px-4 py-2 rounded-md bg-gray-200 text-sm hover:bg-gray-300"
            >
              Back
            </button>

            <div className="flex gap-3">

              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-md bg-gray-200 text-sm hover:bg-gray-300"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>

              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
              >
                {saving ? "Saving..." : "Save changes"}
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};